import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Menu, Search, Home, ChevronDown } from 'lucide-react';
import CurrencySwitcher from '../shared/CurrencySwitcher';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { RootState, AppDispatch } from '../../store';
import { clearCredentials } from '../../store/slices/authSlice';
import { setSearchQuery } from '../../store/slices/searchSlice';
import { fetchFitCoinBalance } from '../../store/slices/tryOnSlice';
import FitCoinsBalance from '../fitcoins/FitCoinsBalance';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { useProtectedNavigation } from '../../hooks/useProtectedNavigation';

// Public Navigation Links
const PublicNavLinks = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  const location = useLocation();
  
  return (
    <div className="flex items-center gap-10">
      <Link
        to="/archives"
        className={`text-[15px] font-semibold transition-colors font-headline ${
          location.pathname === '/archives'
            ? 'text-gray-900'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        Explore
      </Link>
      <Link
        to={isAuthenticated ? "/account/portfolio" : "/join"}
        className={`text-[15px] font-semibold transition-colors font-headline ${
          (location.pathname === '/account/portfolio' || location.pathname === '/join')
            ? 'text-gray-900'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        Create
      </Link>
      <Link
        to="/designers"
        className={`text-[15px] font-semibold transition-colors font-headline ${
          location.pathname === '/designers'
            ? 'text-gray-900'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        Designers
      </Link>
    </div>
  );
};

// Private Navigation Links
const PrivateNavLinks = () => {
  const location = useLocation();
  const { navigateToProtectedRoute } = useProtectedNavigation();
  
  return (
    <div className="flex items-center gap-10">
      <Link
        to="/archives"
        className={`text-[15px] font-semibold transition-colors font-headline ${
          location.pathname === '/archives'
            ? 'text-gray-900'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        Explore
      </Link>
      <a
        href="/account/portfolio"
        onClick={(e) => navigateToProtectedRoute('/account/portfolio', e)}
        className={`text-[15px] font-semibold transition-colors font-headline cursor-pointer ${
          location.pathname === '/account/portfolio'
            ? 'text-gray-900'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        Create
      </a>
      <a
        href="/"
        onClick={(e) => navigateToProtectedRoute('/', e)}
        className={`text-[15px] font-semibold transition-colors font-headline cursor-pointer ${
          location.pathname === '/'
            ? 'text-gray-900'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        Home
      </a>
    </div>
  );
};

// Mobile Navigation Links
const MobileNavLinks = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  const location = useLocation();
  const { navigateToProtectedRoute } = useProtectedNavigation();
  
  return (
    <div className="pt-2 pb-3 space-y-1">
      {isAuthenticated ? (
        <>
          <Link
            to="/archives"
            className={`block px-3 py-2 text-base font-medium ${
              location.pathname === '/archives'
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            } font-headline`}
          >
            Explore
          </Link>
          <a
            href="/account/portfolio"
            onClick={(e) => navigateToProtectedRoute('/account/portfolio', e)}
            className={`block px-3 py-2 text-base font-medium cursor-pointer ${
              location.pathname === '/account/portfolio'
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            } font-headline`}
          >
            Create
          </a>
          <a
            href="/"
            onClick={(e) => navigateToProtectedRoute('/', e)}
            className={`block px-3 py-2 text-base font-medium cursor-pointer ${
              location.pathname === '/'
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            } font-headline`}
          >
            Home
          </a>
        </>
      ) : (
        <>
          <Link
            to="/archives"
            className={`block px-3 py-2 text-base font-medium ${
              location.pathname === '/archives'
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            } font-headline`}
          >
            Explore
          </Link>
          <Link
            to="/join"
            className={`block px-3 py-2 text-base font-medium ${
              location.pathname === '/join'
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            } font-headline`}
          >
            Create
          </Link>
          <Link
            to="/designers"
            className={`block px-3 py-2 text-base font-medium ${
              location.pathname === '/designers'
                ? 'bg-gray-50 text-gray-900'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            } font-headline`}
          >
            Designers
          </Link>
        </>
      )}
    </div>
  );
};

// Currency Dropdown Component
const CurrencyDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { currency, meta } = useCurrencyFormat();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="h-9 pl-2.5 pr-2 rounded-full bg-[#e9b306]/10 text-[#c99a00] hover:bg-[#e9b306]/20 border border-[#e9b306]/20 inline-flex items-center gap-1.5 transition-colors text-[13px] font-bold"
        aria-label={`Currency: ${currency}. Click to change`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <span className="inline-flex items-center justify-center w-4 text-center">
          {meta.symbol}
        </span>
        <span className="tracking-wide">{currency}</span>
        <ChevronDown
          size={14}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
          <CurrencySwitcher onChange={() => setIsOpen(false)} />
        </div>
      )}
    </div>
  );
};

// User Menu Component
const UserMenu = ({ user, isUserMenuOpen, setIsUserMenuOpen, onLogout }: { 
  user: { fullName: string; role: string; profilePicture?: string };
  isUserMenuOpen: boolean;
  setIsUserMenuOpen: (value: boolean) => void;
  onLogout: () => void;
}) => (
  <div className="relative">
    <button
      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
      className="flex items-center space-x-3 focus:outline-none"
    >
      <img
        className="h-8 w-8 rounded-full object-cover"
        src={user.profilePicture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.fullName)}&background=F1D302&color=fff`}
        // alt={user.fullName}
      />
      {/* <span className="text-sm font-headline text-gray-700 font-body">{user.fullName}</span> */}
      <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    </button>

    {isUserMenuOpen && (
      <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
        <Link
          to="/"
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          <Home size={16} />
          Home
        </Link>
        <div className="my-1 border-t border-gray-100" />
        {/* <Link
          to="/profile"
          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          Profile
        </Link>
        <Link
          to="/settings"
          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          Settings
        </Link> */}
        <button
          onClick={onLogout}
          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          Sign out
        </button>
      </div>
    )}
  </div>
);

interface NavbarProps {
  showSidebarToggle?: boolean;
  isMobileSidebarOpen?: boolean;
  onMobileSidebarToggle?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ showSidebarToggle = false, isMobileSidebarOpen = false, onMobileSidebarToggle }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isAuthLoading = useSelector((state: RootState) => state.auth.isLoading);
  const balanceData = useSelector((state: RootState) => state.tryOn.balanceData);
  const { openLoginModal, openSignupModal } = useAuthModal();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const searchQuery = useSelector((state: RootState) => state.search.query);
  
  // Don't render authenticated content while loading
  const showAuthenticatedContent = isAuthenticated && !isAuthLoading && user;

  // Fetch FitCoin balance on mount and when user authenticates
  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(fetchFitCoinBalance());
    }
  }, [isAuthenticated, user, dispatch]);

  const handleLogout = async () => {
    const location = window.location.pathname;
    
    dispatch(clearCredentials());
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
    
    // Store current location to return after re-login (except for protected routes)
    const protectedRoutes = ['/insights', '/account', '/create'];
    const isProtectedRoute = protectedRoutes.some(route => location.startsWith(route));
    
    if (!isProtectedRoute && location !== '/') {
      localStorage.setItem('redirectAfterLogin', location);
    }
    
    const handleLoginRedirect = () => {
      const location = window.location.pathname;
      
      // Save the current path to redirect after login
      if (location !== '/' && location !== '/login') {
        localStorage.setItem('redirectAfterLogin', location);
      }
      
      openLoginModal();
    };

    handleLoginRedirect();
  };


  return (
    <>
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 hidden md:block">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center gap-3">
            {/* Mobile Sidebar Toggle - Only on mobile when sidebar should be shown */}
            {showSidebarToggle && !isMobileSidebarOpen && (
              <button
                onClick={onMobileSidebarToggle}
                className="md:hidden p-2 bg-white rounded-lg shadow-lg border border-gray-200 hover:bg-[#e9b306] hover:text-white transition-colors"
              >
                <Menu size={20} />
              </button>
            )}
            <Link to="/">
              <img 
                src="/Creatorsarchive1.2@4x.png" 
                alt="Creators Archive" 
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {/* Navigation Links - Large Desktop Only */}
          <div className="hidden xl:flex items-center space-x-8 ml-12">
            {showAuthenticatedContent ? <PrivateNavLinks /> : <PublicNavLinks isAuthenticated={isAuthenticated} />}
          </div>

          {/* Search Bar - Pinterest Style */}
          <div className="flex-1 max-w-2xl mx-8 hidden md:block">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 stroke-[1.5]" strokeLinecap="round" strokeLinejoin="round" />
              <input
                type="text"
                placeholder="Search for African fashion inspiration"
                value={searchQuery}
                onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                className="w-full pl-12 pr-4 py-3 bg-gray-100 rounded-full border-0 focus:outline-none focus:ring-2 focus:ring-[#E9B306] focus:bg-white transition-all"
              />
            </div>
          </div>

          {/* Auth Buttons / User Menu - Hidden on Mobile */}
          <div className="hidden md:flex items-center space-x-3">
            {isAuthLoading ? (
              <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
            ) : showAuthenticatedContent ? (
              <>
                <FitCoinsBalance 
                  balanceData={balanceData}
                  compact
                />
                <CurrencyDropdown />
                <UserMenu 
                  user={{
                    fullName: user.fullName || 'User',
                    role: user.role,
                    profilePicture: user.profilePicture
                  }} 
                  isUserMenuOpen={isUserMenuOpen} 
                  setIsUserMenuOpen={setIsUserMenuOpen}
                  onLogout={handleLogout}
                />
              </>
            ) : (
              <>
                <button
                  onClick={() => openLoginModal()}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-headline"
                >
                  Log in
                </button>
                <button
                  onClick={() => openSignupModal()}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-headline"
                >
                  Join as a Designer
                </button>
              </>
            )}
          </div>

          {/* Animated Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="xl:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors relative"
          >
            <span className="sr-only">Toggle menu</span>
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span className={`block h-0.5 w-6 bg-current transition-all duration-300 ease-out ${
                isMenuOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-1.5'
              }`}></span>
              <span className={`block h-0.5 w-6 bg-current transition-all duration-300 ease-out ${
                isMenuOpen ? 'opacity-0' : 'opacity-100'
              }`}></span>
              <span className={`block h-0.5 w-6 bg-current transition-all duration-300 ease-out ${
                isMenuOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-1.5'
              }`}></span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`xl:hidden border-t border-gray-100 bg-white/95 backdrop-blur-sm transition-all duration-300 ease-out overflow-hidden ${
        isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
          {/* Mobile Search */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 stroke-[1.5]" strokeLinecap="round" strokeLinejoin="round" />
              <input
                type="text"
                placeholder="Search for African fashion inspiration"
                value={searchQuery}
                onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                className="w-full pl-12 pr-4 py-3 bg-gray-100 rounded-full border-0 focus:outline-none focus:ring-2 focus:ring-[#E9B306] focus:bg-white transition-all"
              />
            </div>
          </div>

          <MobileNavLinks isAuthenticated={!!showAuthenticatedContent} />
          
          {isAuthLoading ? (
            <div className="px-4 py-4 border-t border-gray-100 flex justify-center">
              <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
            </div>
          ) : showAuthenticatedContent ? (
            <div className="px-4 py-4 border-t border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <img
                  className="h-10 w-10 rounded-full object-cover"
                  src={user?.profilePicture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.fullName || '')}&background=F1D302&color=fff`}
                  alt={user?.fullName}
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900 font-headline">{user?.fullName}</p>
                  <p className="text-xs text-gray-500 font-body">{user?.role}</p>
                </div>
              </div>
              <div className="mb-3 px-1">
                <CurrencyDropdown />
              </div>
              <div className="space-y-1">
                <Link
                  to="/account/settings"
                  className="block px-4 py-2.5 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors font-body"
                >
                  Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors font-body"
                >
                  Sign out
                </button>
              </div>
            </div>
          ) : (
            <div className="px-4 py-4 border-t border-gray-100 space-y-2">
              <div className="mb-2">
                <CurrencyDropdown />
              </div>
              <button 
                onClick={() => {
                  setIsMenuOpen(false);
                  openLoginModal();
                }}
                className="block w-full text-center px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors font-headline"
              >
                Log in
              </button>
              <button 
                onClick={() => {
                  setIsMenuOpen(false);
                  openSignupModal();
                }}
                className="block w-full text-center px-4 py-2.5 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors font-headline"
              >
                Join Creators Archive
              </button>
            </div>
          )}
        </div>
    </header>
    </>
  );
};

export default Navbar; 